<template>
  <b-modal ref="modal" title="Correct reading" centered no-close-on-backdrop no-close-on-esc hide-footer>
    <div v-if="row">
      <div class="alert alert-warning">
        <i class="fas fa-exclamation-triangle mr-2"></i>
        The original bill for this lot will be <strong>voided</strong> and replaced by a new bill at the same
        rates. If the tenant already received the bill, they will be emailed the corrected invoice together with
        the cancelled one.
      </div>
      <p class="mb-2">
        Lot <strong>{{ row.lot && row.lot.lot_no }}</strong>
        &middot; opening reading <strong>{{ row.previous_unit }}</strong>
        &middot; current closing reading <strong>{{ row.current_unit }}</strong>
      </p>
      <b-form-group label="New closing reading" label-for="correctNewReading"
        description="The corrected meter reading, not the consumption.">
        <b-form-input id="correctNewReading" v-model="newReading" type="text" inputmode="decimal" autocomplete="off"
          :disabled="loading" :state="newReading === '' ? null : !readingError"></b-form-input>
        <small v-if="newReading !== '' && readingError" class="text-danger">{{ readingError }}</small>
      </b-form-group>
      <b-form-group label="Reason (optional)" label-for="correctReason"
        description="Shown to the tenant in the email.">
        <b-form-input id="correctReason" v-model="reason" maxlength="255" :placeholder="reasonPlaceholder"
          :disabled="loading"></b-form-input>
      </b-form-group>
      <b-form-group label="Photo (optional)" label-for="correctPhoto" description="Evidence photo, max 10 MB.">
        <b-form-file id="correctPhoto" v-model="photo" accept="image/*" placeholder="Choose a photo..."
          :disabled="loading"></b-form-file>
        <small v-if="photoError" class="text-danger">{{ photoError }}</small>
      </b-form-group>
      <div v-if="errorMessage" class="alert alert-danger">
        <p class="mb-1">{{ errorMessage }}</p>
        <ul v-if="errorList.length" class="mb-0 pl-3 small">
          <li v-for="e in errorList" :key="e">{{ e }}</li>
        </ul>
      </div>
      <div class="d-flex justify-content-end">
        <b-button variant="secondary" class="mr-2" :disabled="loading" @click="hide">Cancel</b-button>
        <b-button variant="warning" :disabled="loading || !!readingError || !!photoError || newReading === ''"
          @click="submit">
          <b-spinner v-if="loading" small></b-spinner>
          <span v-else>Correct reading</span>
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import ApiServices from '@/services/api'
import { normalizeReading } from '@/utils/readings'
import { correctBlockedMessage, validateNewReading } from '@/utils/rowCorrection'

const MAX_PHOTO_BYTES = 10 * 1024 * 1024

export default {
  // Dialog for correcting one lot's closing reading in a closed session.
  // Parent calls `$refs.x.show(row)`; emits `corrected` after a successful correction.
  data() {
    return {
      row: null,
      newReading: '',
      reason: '',
      photo: null,
      loading: false,
      errorMessage: '',
      errorList: []
    }
  },
  computed: {
    readingError() {
      return validateNewReading(this.newReading, this.row)
    },
    photoError() {
      if (!this.photo) return ''
      if (this.photo.type && this.photo.type.indexOf('image/') !== 0) return 'The file must be an image.'
      if (this.photo.size > MAX_PHOTO_BYTES) return 'The photo must be 10 MB or smaller.'
      return ''
    },
    reasonPlaceholder() {
      const next = this.newReading === '' ? '{new}' : this.newReading
      return 'Reading changed from ' + (this.row ? this.row.current_unit : '{old}') + ' to ' + next
    }
  },
  methods: {
    // Opens the dialog for a lot row, resetting any previous input. 2026-10-19
    show(row) {
      this.row = row
      this.newReading = ''
      this.reason = ''
      this.photo = null
      this.errorMessage = ''
      this.errorList = []
      this.$refs.modal.show()
    },
    hide() {
      this.$refs.modal.hide()
    },
    // Submits the correction; on failure keeps the dialog open with the server message and still asks the parent to refetch (canCorrect may be stale). 2026-10-19
    submit() {
      this.loading = true
      this.errorMessage = ''
      this.errorList = []
      ApiServices.correctSessionRow({
        archived_session_id: this.row.id,
        new_reading: normalizeReading(this.newReading),
        reason: this.reason.trim(),
        meter_image: this.photo
      })
        .then(response => {
          let msg = (response && response.message) || 'Row corrected.'
          if (response && response.void_mail === 'queued') {
            msg += ' The tenant will be emailed the corrected invoice.'
          }
          this.SUCCESS_TOAST_MSG(msg)
          this.hide()
          this.$emit('corrected')
        })
        .catch(err => {
          // 404/500 aren't toasted usefully by the interceptor, so always show inline.
          this.errorMessage = (err && err.reason) ? correctBlockedMessage(err.reason) : ((err && err.message) || 'Row correction failed.')
          const errors = (err && err.errors) || {}
          this.errorList = Object.keys(errors).reduce((all, k) => all.concat(errors[k]), [])
          this.$emit('failed')
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/Common/correctRowModal.vue